import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useGetAiworkersPresignedUrlMutation, useUploadFileToS3Mutation } from '@circuitry-ai/doc-data';
import { CtyShowToast } from '../toast/CtyToast';
import { CLAIM_INTAKE_GENERIC_ERROR_MESSAGE } from './claimSubmitErrorUtils';
import { ClaimIntakeFileUploadCard } from './ClaimIntakeFileUploadCard';
import {
  buildAdditionalSourcesFromGenerateResponse,
  removeAdditionalSourcesForFile,
  type AdditionalSourceItem,
} from './additionalInformationUpload';
import { uploadClaimIntakeFilesViaPresigned } from './claimIntakeRum';

function isSameUploadedFile(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

export function AdditionalInformationUploadSection({
  files,
  onFilesChange,
  additionalSources,
  onAdditionalSourcesChange,
  requirementChips,
}: Readonly<{
  files: File[];
  onFilesChange: Dispatch<SetStateAction<File[]>>;
  additionalSources: AdditionalSourceItem[];
  onAdditionalSourcesChange: Dispatch<SetStateAction<AdditionalSourceItem[]>>;
  requirementChips?: readonly string[];
}>) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [getAiworkersPresignedUrl] = useGetAiworkersPresignedUrlMutation();
  const [uploadToS3] = useUploadFileToS3Mutation();

  const processFiles = useCallback(
    async (incomingFiles: File[]) => {
      const newFiles = incomingFiles.filter(
        (file) => !files.some((existing) => isSameUploadedFile(existing, file))
      );
      if (newFiles.length === 0) return;

      setIsUploading(true);
      setUploadError(null);

      try {
        const { urlList } = await uploadClaimIntakeFilesViaPresigned({
          files: newFiles,
          purpose: 'additional_info',
          getPresignedUrls: (payload) => getAiworkersPresignedUrl(payload).unwrap(),
          uploadOne: (file, presignedUrl) =>
            uploadToS3({ file, presignedUrl, CtyShowToast }).unwrap(),
        });

        const newSources = buildAdditionalSourcesFromGenerateResponse(urlList, newFiles);
        onFilesChange((prev) => [...prev, ...newFiles]);
        onAdditionalSourcesChange((prev) => [...prev, ...newSources]);
        CtyShowToast('success',`${newFiles.length} file(s) added to additional information.`);
      } catch (err: unknown) {
        const message = CLAIM_INTAKE_GENERIC_ERROR_MESSAGE;
        void err;
        setUploadError(message);
        CtyShowToast('error', message);
      } finally {
        setIsUploading(false);
      }
    },
    [files, getAiworkersPresignedUrl, uploadToS3, onFilesChange, onAdditionalSourcesChange]
  );

  const handleFilesChosen = useCallback(
    (chosen: File[]) => {
      if (chosen.length > 0) void processFiles(chosen);
    },
    [processFiles]
  );

  const handleFileDrop = useCallback(
    (e: React.DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (isUploading) return;
      handleFilesChosen(Array.from(e.dataTransfer.files ?? []));
    },
    [handleFilesChosen, isUploading]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const chosen = Array.from(e.target.files ?? []);
      e.target.value = '';
      handleFilesChosen(chosen);
    },
    [handleFilesChosen]
  );

  const isTirePhotoUpload = Boolean(requirementChips && requirementChips.length > 0);

  const removeFile = useCallback(
    (index: number) => {
      const file = files[index];
      if (!file) return;
      onFilesChange((prev) => prev.filter((_, i) => i !== index));
      onAdditionalSourcesChange((prev) => removeAdditionalSourcesForFile(prev, file));
    },
    [files, onFilesChange, onAdditionalSourcesChange]
  );

  return (
    <ClaimIntakeFileUploadCard
      title='Supporting Documents'
      description={
        isTirePhotoUpload
          ? 'To process the claim, we need the following photos:'
          : 'Upload any extra supporting files.'
      }
      isUploading={isUploading}
      loadingMessage='Uploading files…'
      idleTitle="Click or drop any additional supporting documents or media"
      idleHint="All file types accepted — multiple files supported"
      idleIcon="mdi:paperclip-plus"
      chips={requirementChips}
      uploadError={uploadError}
      uploadedFiles={files}
      onFileDrop={handleFileDrop}
      onFileSelect={handleFileSelect}
      onPickFile={() => fileInputRef.current?.click()}
      onRemoveFile={removeFile}
      fileInputRef={fileInputRef}
      footerExtra={
        additionalSources.length > 0 ? (
          <p className="mt-2 text-xs text-default-400">
            {additionalSources.length} source(s) attached to the claim.
          </p>
        ) : null
      }
    />
  );
}
